let _taskIdCounter = Date.now();
function makeTaskId() { return ++_taskIdCounter; }

export function makeTask(name, isPunchList = false) {
  return { id: makeTaskId(), name, status: "Not Started", assignedTo: "", startDate: "", dueDate: "", dateCompleted: "", notes: "", durationDays: null, predecessors: [], isPunchList, parentId: null, isPhaseHeader: false };
}
export function makeTasks(names) { return names.map(makeTask); }

export function makeTasksFromTemplate(templateTasks) {
  return templateTasks.map(tt => ({
    id: makeTaskId(),
    name: tt.name,
    status: "Not Started",
    assignedTo: "",
    startDate: "",
    dueDate: "",
    dateCompleted: "",
    notes: "",
    durationDays: tt.durationDays ?? null,
    predecessors: [],
    estimatedCost: tt.estimatedCost ?? null,
    actualCost: null,
    parentId: null,
    parentRefId: tt.parentRefId || null,
    isPhaseHeader: tt.isPhase || false,
  }));
}

export function pct(tasks) {
  if (!tasks.length) return 0;
  // Exclude phase tasks (tasks that have children OR are marked as phase headers) from completion calculation
  const parentIds = new Set(tasks.map(t => t.parentId).filter(Boolean));
  const leafTasks = tasks.filter(t => !parentIds.has(t.supabaseId) && !t.isPhaseHeader);
  if (!leafTasks.length) return 0;
  return Math.round((leafTasks.filter(t => t.status === "Complete").length / leafTasks.length) * 100);
}

/**
 * Returns true if a task has a due date in the past and is not Complete.
 * Uses noon-anchoring (T12:00:00) for timezone safety — the same convention
 * every date comparison in this app uses, to avoid UTC vs local-day drift.
 */
export function isOverdue(task, referenceDate = new Date()) {
  if (!task?.dueDate || task.status === "Complete") return false;
  return new Date(task.dueDate + "T12:00:00") < referenceDate;
}

/**
 * Format a date string or Date object to US format.
 * @param {string|Date} date — ISO string, date string, or Date object
 * @param {object} [opts] — Intl.DateTimeFormat options override
 * @returns {string} e.g. "Jan 15, 2026" or "—" if falsy
 */
export function formatDate(date, opts) {
  if (!date) return '—';
  try {
    const d = date instanceof Date ? date : new Date(typeof date === 'string' && !date.includes('T') ? date + 'T12:00:00' : date);
    if (isNaN(d)) return '—';
    return d.toLocaleDateString('en-US', opts || { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

export function formatCurrency(amount) {
  if (amount == null) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

export function exportData(projects, contacts, templates) {
  const data = { version: 1, exportedAt: new Date().toISOString(), projects, contacts, templates };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `baulit-backup-${new Date().toISOString().split("T")[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function importData(file, onSuccess, onError) {
  const reader = new FileReader();
  reader.onload = e => {
    try {
      const data = JSON.parse(e.target.result);
      if (!Array.isArray(data.projects) || !Array.isArray(data.contacts) || !data.templates) {
        onError("Invalid backup file — missing required fields.");
        return;
      }
      onSuccess(data);
    } catch { onError("Could not read file. Make sure it's a valid Baulit backup."); }
  };
  reader.readAsText(file);
}

export function buildBudgetCSVRows(project) {
  const rows = [
    ["Project", "Task", "Category", "Description", "Estimated", "Actual", "Variance"],
  ];
  let grandEstimated = 0;
  let grandActual = 0;

  for (const t of project.tasks) {
    const costs = t.costs || [];
    if (costs.length === 0) {
      // Task with no line items — export a single row if it has legacy cost data
      if (t.estimatedCost != null || t.actualCost != null) {
        const est = t.estimatedCost || 0;
        const act = t.actualCost || 0;
        grandEstimated += est;
        grandActual += act;
        rows.push([project.name, t.name, "", "", est, act, est - act]);
      }
      continue;
    }
    let taskEst = 0;
    let taskAct = 0;
    for (const c of costs) {
      const est = c.estimated || 0;
      const act = c.actual || 0;
      taskEst += est;
      taskAct += act;
      rows.push([project.name, t.name, c.category || "", c.description || "", est, act, est - act]);
    }
    // Task subtotal row
    rows.push(["", t.name + " SUBTOTAL", "", "", taskEst, taskAct, taskEst - taskAct]);
    grandEstimated += taskEst;
    grandActual += taskAct;
  }

  // Change Orders section
  const changeOrders = project.changeOrders || [];
  if (changeOrders.length > 0) {
    rows.push([]);
    rows.push(["CHANGE ORDERS", "", "", "", "", "", ""]);
    rows.push(["CO #", "Title", "Status", "Linked Task", "Original", "New", "Delta"]);
    let netDelta = 0;
    for (const co of changeOrders) {
      const linkedTask = co.taskId ? (project.tasks || []).find(t => t.id === co.taskId || t.supabaseId === co.taskId) : null;
      rows.push([
        co.coNumber,
        co.title,
        co.status,
        linkedTask ? linkedTask.name : "",
        co.originalAmount || 0,
        co.newAmount || 0,
        co.delta || 0,
      ]);
      if (co.status === "approved") netDelta += (co.delta || 0);
    }
    rows.push(["NET APPROVED DELTA", "", "", "", "", "", netDelta]);
  }

  rows.push([]);
  rows.push(["GRAND TOTAL", "", "", "", grandEstimated, grandActual, grandEstimated - grandActual]);

  return rows;
}

export function exportBudgetCSV(project) {
  const rows = buildBudgetCSVRows(project);

  const csv = rows
    .map(r => r.map(v => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    }).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = project.name.replace(/[^a-zA-Z0-9_-]/g, "_");
  a.download = `${safeName}-budget-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg', 'image/png', 'image/heic', 'image/heif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

/**
 * Optimistic locking: update a row only if updated_at matches the previously loaded value.
 * Returns { ok, data, conflict, error }.
 */
export async function conflictCheckedUpdate(supabase, table, id, payload, previousUpdatedAt) {
  let query = supabase.from(table).update(payload).eq("id", id)
  if (previousUpdatedAt) query = query.eq("updated_at", previousUpdatedAt)
  const { data, error } = await query.select()
  if (error) return { ok: false, error }
  if (!data || data.length === 0) return { ok: false, conflict: true }
  return { ok: true, data: data[0] }
}

export function compressImage(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/') || file.type === 'image/heic' || file.type === 'image/heif') {
      resolve(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_SIDE = 1600;
      let { width, height } = img;
      if (width <= MAX_SIDE && height <= MAX_SIDE && file.size < 500_000) {
        resolve(file);
        return;
      }
      if (width > MAX_SIDE || height > MAX_SIDE) {
        const ratio = Math.min(MAX_SIDE / width, MAX_SIDE / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })),
        'image/jpeg',
        0.8
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}
